<template>
  <!-- Hidden entirely when nothing is live or paused, to keep the Delegator compact -->
  <div v-if="manageable.length" class="border rounded-2xl bg-white shadow overflow-hidden dark:bg-gray-600 dark:border-gray-600 mb-2">
    <button type="button" @click="expanded = !expanded"
      class="w-full flex items-center gap-2 px-4 py-2 text-sm font-bold text-gray-900 dark:text-gray-100">
      <svg class="h-4 w-4 transition-transform" :class="expanded ? '' : '-rotate-90'" viewBox="0 0 20 20" fill="currentColor">
        <path d="M6 8l4 4 4-4" />
      </svg>
      Active broadcasts
      <span class="text-red-600">({{ manageable.length }})</span>
      <span class="flex-1"></span>
    </button>

    <div v-if="expanded" class="border-t border-gray-200 dark:border-gray-700">
      <div class="overflow-x-auto">
        <table class="w-full text-xs text-left text-gray-500 dark:text-gray-200">
          <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-300">
            <tr>
              <th class="px-3 py-2">Live</th>
              <th class="px-3 py-2">Type</th>
              <th class="px-3 py-2">Client</th>
              <th class="px-3 py-2">Subject</th>
              <th class="px-3 py-2">Ticket</th>
              <th class="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="b in manageable" :key="b.id">
              <td class="px-3 py-2">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" class="sr-only peer" :checked="b.active" :disabled="busyId !== null" @change="toggle(b, $event)" />
                  <div class="w-9 h-5 bg-gray-300 rounded-full peer peer-checked:bg-green-500 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
                </label>
              </td>
              <td class="px-3 py-2">
                <span :class="pillClass(b.type)">{{ b.type === 'outage' ? 'Outage' : 'Info' }}</span>
              </td>
              <td class="px-3 py-2 text-gray-700 dark:text-gray-100">{{ clientNames(b) }}</td>
              <td class="px-3 py-2">{{ b.subject }}</td>
              <td class="px-3 py-2">
                <a v-if="b.ticket_id" :href="`/ticket/${b.ticket_id}`" target="_blank"
                  class="text-blue-600 dark:text-blue-300 hover:underline">#{{ b.ticket_id }}</a>
                <span v-else class="text-gray-400">—</span>
              </td>
              <td class="px-3 py-2 whitespace-nowrap">
                <button @click="$emit('edit', b)" :disabled="busyId !== null"
                  class="text-blue-600 hover:text-blue-800 underline mr-3 disabled:opacity-50 disabled:cursor-not-allowed">Edit</button>
                <button @click="end(b)" :disabled="busyId !== null"
                  class="text-red-600 hover:text-red-800 underline disabled:opacity-50 disabled:cursor-not-allowed">
                  {{ busyId === b.id ? 'Ending…' : 'End now' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p class="px-4 py-2 text-xs text-gray-400">
          The Live switch pauses/resumes a broadcast without deleting it. Outage broadcasts also end automatically when their ticket is closed.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import BroadcastService from '@/services/BroadcastService';

const props = defineProps({
  // Bump this to force a reload (e.g. after creating a broadcast).
  reloadSignal: { type: Number, default: 0 },
});
const emit = defineEmits(['active-companies', 'edit']);

const { proxy } = getCurrentInstance();
const updateLoading = (n) => proxy?.$updateLoading?.(n);

const expanded = ref(false);
const broadcasts = ref([]);
// Id of the broadcast with a toggle/end request in flight — blocks repeat clicks.
const busyId = ref(null);

// Manageable = not yet ended (live or paused). Ended ones drop off the list.
const manageable = computed(() => broadcasts.value.filter(b => !b.ended_at));

const isLive = (b) => b.active && !b.ended_at
  && (!b.auto_hide_at || new Date(b.auto_hide_at) > new Date());

const emitActiveCompanies = () => {
  const ids = new Set();
  broadcasts.value.filter(isLive).forEach(b => (b.companies || []).forEach(c => ids.add(Number(c.id))));
  emit('active-companies', [...ids]);
};

const load = async () => {
  try {
    const { data } = await BroadcastService.list();
    broadcasts.value = Array.isArray(data) ? data : (data.data || []);
    emitActiveCompanies();
  } catch (e) {
    // Non-fatal — the delegator still works without the panel.
  }
};

const toggle = async (b, event) => {
  if (busyId.value !== null) return;
  busyId.value = b.id;
  updateLoading(1);
  try {
    const { data } = await BroadcastService.toggle(b.id);
    Object.assign(b, data);
    emitActiveCompanies();
  } catch (e) {
    // Leave state as-is on failure — snap the switch back to the saved value.
    if (event?.target) event.target.checked = b.active;
  } finally {
    busyId.value = null;
    updateLoading(-1);
  }
};

const end = async (b) => {
  if (busyId.value !== null) return;
  busyId.value = b.id;
  updateLoading(1);
  try {
    await BroadcastService.end(b.id);
    b.ended_at = new Date().toISOString();
    emitActiveCompanies();
  } catch (e) {
    /* no-op */
  } finally {
    busyId.value = null;
    updateLoading(-1);
  }
};

const clientNames = (b) => b.all_companies
  ? 'All clients'
  : (b.companies || []).map(c => c.shortname || c.companyname).join(' · ');

const pillClass = (type) => {
  const base = 'inline-block rounded-full px-2 py-0.5 text-xs font-bold text-white';
  return type === 'outage' ? `${base} bg-red-500` : `${base} bg-blue-600`;
};

onMounted(load);
watch(() => props.reloadSignal, load);
</script>
